import { defineStore } from 'pinia'
import api from '@/services/api'
import { format, parseISO, startOfDay, endOfDay, isWithinInterval } from 'date-fns'
import { fr } from 'date-fns/locale'

export const usePointageStore = defineStore('pointage', {
  state: () => ({
    pointages: [],
    filteredPointages: [],
    agentPointages: [],
    loading: false,
    loadingAgentPointages: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      totalItems: 0,
      totalPages: 0
    },
    searchQuery: '',
    selectedDate: format(new Date(), 'yyyy-MM-dd'),
    selectedDirection: 'Toutes',
    selectedMotif: 'Tous',
    stats: {
      totalToday: 0,
      totalPresent: 0,
      totalAbsent: 0,
      totalLate: 0
    }
  }),

  getters: {
    todayPointages: (state) => {
      const today = new Date()
      return state.pointages.filter(p => {
        const pointageDate = parseISO(p.date)
        return isWithinInterval(pointageDate, {
          start: startOfDay(today),
          end: endOfDay(today)
        })
      })
    },
    
    getPointagesByDay: (state) => (date) => {
      const targetDate = parseISO(date)
      return state.pointages.filter(p => {
        const pointageDate = parseISO(p.date)
        return isWithinInterval(pointageDate, {
          start: startOfDay(targetDate),
          end: endOfDay(targetDate)
        })
      })
    },
    
    getPointagesByDateRange: (state) => (startDate, endDate) => {
      const start = parseISO(startDate)
      const end = parseISO(endDate)
      return state.pointages.filter(p => {
        const pointageDate = parseISO(p.date)
        return isWithinInterval(pointageDate, {
          start: startOfDay(start),
          end: endOfDay(end)
        })
      })
    },
    
    getPointagesByAgentId: (state) => (agentId) => {
      return state.pointages.filter(p => p.agent.id === agentId)
    },
    
    getStatsByMotif: (state) => {
      const stats = {}
      
      state.pointages.forEach(pointage => {
        const motif = pointage.motif || 'Non défini'
        stats[motif] = (stats[motif] || 0) + 1
      })
      
      return Object.entries(stats).map(([motif, total]) => ({
        motif,
        total
      }))
    },
    
    getStatsByDirection: (state) => {
      const stats = {}
      
      state.pointages.forEach(pointage => {
        const dir = pointage.agent?.direction || 'Non défini'
        stats[dir] = (stats[dir] || 0) + 1
      })
      
      return Object.entries(stats).map(([direction, total]) => ({
        direction,
        total
      }))
    },
    
    paginatedPointages: (state) => {
      return state.filteredPointages.slice(
        (state.pagination.page - 1) * state.pagination.limit,
        state.pagination.page * state.pagination.limit
      )
    },
    
    getMotifList: (state) => {
      const motifs = [...new Set(state.pointages.map(p => p.motif).filter(Boolean))]
      return ['Tous', ...motifs]
    }
  },

  actions: {
    async fetchPointages() {
      this.loading = true
      this.error = null
      
      try {
        const response = await api.getPointages()
        this.pointages = response.data
        this.updateStats()
        this.applyFilters()
      } catch (error) {
        this.error = error.message || 'Erreur lors du chargement des pointages'
        console.error('Erreur fetchPointages:', error)
      } finally {
        this.loading = false
      }
    },
    
    async fetchPointagesForDate(date) {
      this.loading = true
      this.error = null
      this.selectedDate = date
      
      try {
        const formattedDate = format(parseISO(date), 'yyyy-MM-dd')
        const response = await api.getPointagesForDate(formattedDate)
        // Mise à jour des pointages existants ou ajout de nouveaux
        const existingIds = new Set(this.pointages.map(p => p.id))
        response.data.forEach(pointage => {
          if (!existingIds.has(pointage.id)) {
            this.pointages.push(pointage)
          }
        })
        this.updateStats()
        this.applyFilters()
        return response.data
      } catch (error) {
        this.error = error.message || `Erreur lors du chargement des pointages pour ${date}`
        console.error('Erreur fetchPointagesForDate:', error)
        return []
      } finally {
        this.loading = false
      }
    },
    
    async fetchPointagesByAgentId(agentId, startDate, endDate) {
      this.loading = true
      this.error = null
      
      try {
        const response = await api.getPointagesByAgentId(
          agentId, 
          format(parseISO(startDate), 'yyyy-MM-dd'),
          format(parseISO(endDate), 'yyyy-MM-dd')
        )
        return response.data
      } catch (error) {
        this.error = error.message || `Erreur lors du chargement des pointages pour l'agent ${agentId}`
        console.error('Erreur fetchPointagesByAgentId:', error)
        return []
      } finally {
        this.loading = false
      }
    },
    
    async fetchAgentPointages({ agentId, startDate, endDate }) {
      this.loadingAgentPointages = true
      this.error = null
      
      try {
        // Si nous avons déjà chargé tous les pointages et avons celui pour cet agent,
        // nous pouvons filtrer localement
        if (this.pointages.length > 0) {
          // Vérifier si nous avons déjà des pointages pour cet agent dans la période demandée
          const start = parseISO(startDate)
          const end = parseISO(endDate)
          
          const existingPointages = this.pointages.filter(p => {
            const isCorrectAgent = p.agent.num_personnel === agentId
            if (!isCorrectAgent) return false
            
            const pointageDate = parseISO(p.date)
            const isInDateRange = isWithinInterval(pointageDate, {
              start: startOfDay(start),
              end: endOfDay(end)
            })
            
            return isInDateRange
          })
          
          // Si nous avons des pointages pour cette période, pas besoin d'appel API
          if (existingPointages.length > 0) {
            this.agentPointages = existingPointages
            return existingPointages
          }
        }
        
        // Sinon, faire l'appel API
        const response = await api.getPointagesByAgentId(
          agentId,
          format(parseISO(startDate), 'yyyy-MM-dd'),
          format(parseISO(endDate), 'yyyy-MM-dd')
        )
        
        // Mettre à jour le state avec les pointages récupérés
        this.agentPointages = response.data
        
        // Ajouter également ces pointages à notre liste principale si pas déjà présents
        const existingIds = new Set(this.pointages.map(p => p.id))
        response.data.forEach(pointage => {
          if (!existingIds.has(pointage.id)) {
            this.pointages.push(pointage)
          }
        })
        
        return response.data
      } catch (error) {
        this.error = error.message || `Erreur lors du chargement des pointages pour l'agent ${agentId}`
        console.error('Erreur fetchAgentPointages:', error)
        this.agentPointages = []
        return []
      } finally {
        this.loadingAgentPointages = false
      }
    },
    
    updateStats() {
      const todayPointages = this.todayPointages
      
      this.stats = {
        totalToday: todayPointages.length,
        totalPresent: todayPointages.filter(p => p.motif === 'PRÉSENT').length,
        totalAbsent: todayPointages.filter(p => ['ABSENT', 'CONGÉ', 'PERMISSION'].includes(p.motif)).length,
        totalLate: todayPointages.filter(p => p.motif === 'RETARD').length
      }
    },
    
    setPage(page) {
      this.pagination.page = page
    },
    
    setSearchQuery(query) {
      this.searchQuery = query
      this.pagination.page = 1
      this.applyFilters()
    },
    
    setSelectedDate(date) {
      this.selectedDate = date
      this.applyFilters()
    },
    
    setSelectedDirection(direction) {
      this.selectedDirection = direction
      this.pagination.page = 1
      this.applyFilters()
    },
    
    setSelectedMotif(motif) {
      this.selectedMotif = motif
      this.pagination.page = 1
      this.applyFilters()
    },
    
    applyFilters() {
      let result = [...this.pointages]
      
      // Filtre par date
      if (this.selectedDate) {
        const targetDate = parseISO(this.selectedDate)
        result = result.filter(p => {
          const pointageDate = parseISO(p.date)
          return isWithinInterval(pointageDate, {
            start: startOfDay(targetDate),
            end: endOfDay(targetDate)
          })
        })
      }
      
      // Filtre par direction
      if (this.selectedDirection !== 'Toutes') {
        result = result.filter(p => p.agent.direction === this.selectedDirection)
      }
      
      // Filtre par motif
      if (this.selectedMotif !== 'Tous') {
        result = result.filter(p => p.motif === this.selectedMotif)
      }
      
      // Filtre par recherche
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(p => {
          return (
            (p.agent.nom && p.agent.nom.toLowerCase().includes(query)) ||
            (p.agent.prenom && p.agent.prenom.toLowerCase().includes(query)) ||
            (p.agent.matricule && p.agent.matricule.toLowerCase().includes(query)) ||
            (p.agent.direction && p.agent.direction.toLowerCase().includes(query)) ||
            (p.motif && p.motif.toLowerCase().includes(query))
          )
        })
      }
      
      // Mise à jour de la pagination
      this.filteredPointages = result
      this.pagination.totalItems = result.length
      this.pagination.totalPages = Math.ceil(result.length / this.pagination.limit)
    },
    
    formatDate(date, formatStr = 'dd MMMM yyyy') {
      return format(parseISO(date), formatStr, { locale: fr })
    },
    
    formatTime(date) {
      return format(parseISO(date), 'HH:mm', { locale: fr })
    },
    
    formatDuration(minutes) {
      if (!minutes) return 'N/A'
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return `${hours}h${mins.toString().padStart(2, '0')}`
    },
    
    // Récupère le nombre de jours travaillés, absents et en retard pour un agent
    // sur une période donnée
    getAgentAttendanceStats(agentId, startDate, endDate) {
      // Si nous n'avons pas les pointages de l'agent, retourner des stats vides
      if (!this.agentPointages.length) {
        return {
          totalDays: 0,
          present: 0,
          absent: 0, 
          late: 0,
          other: 0
        }
      }
      
      // Filtrer les pointages pour la période demandée si nécessaire
      let pointages = this.agentPointages
      if (startDate && endDate) {
        const start = parseISO(startDate)
        const end = parseISO(endDate)
        pointages = pointages.filter(p => {
          const pointageDate = parseISO(p.date)
          return isWithinInterval(pointageDate, {
            start: startOfDay(start),
            end: endOfDay(end)
          })
        })
      }
      
      // Calculer les stats
      const stats = pointages.reduce((acc, record) => {
        const motifLower = record.motif.toLowerCase()
        if (motifLower.includes('présent')) {
          acc.present++
        } else if (motifLower.includes('retard')) {
          acc.late++
        } else if (motifLower.includes('absent') || motifLower.includes('congé') || motifLower.includes('permission')) {
          acc.absent++
        } else {
          acc.other++
        }
        return acc
      }, { present: 0, absent: 0, late: 0, other: 0 })
      
      stats.totalDays = pointages.length
      return stats
    }
  }
})